import React, { useMemo, useState, useEffect } from "react";
import PrimaryHeading from '../../../components/common/primaryHeading';
import DataGrid from '../../../components/common/dataGrid/dataGrid';
import ButtonWithSearch from '../../../components/common/buttonWithSearch';
import InputPrimaryComponents from '../../../components/common/inputPrimaryComponents';
import { useForm } from "react-hook-form";
import "./SendNotification.scss";
import AddButtonSection from '../../../components/common/AddButtonSection';
import { showToast } from '../../../components/common/toast';
import { commonGetApi, commonPostApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../../constants/toast";
import { SORT_ORDER } from "../../../constants/sortOrder";
import { useLocation } from "react-router-dom";

const SendNotification = () => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const location = useLocation();
  const [userData, setUserData] = useState([]);
  const [campData, setCampData] = useState([]);
  // Keep selection across pages by storing IDs; grid selection is derived per-page.
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState([]);
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 0,
    sortBy: "user_name",
    sortDir: SORT_ORDER.ASC,
    sortFields: [["user_name", SORT_ORDER.ASC]],
    searchCriteria: {
      searchvalue: ""
    },
  });

  const selectedCampValue = watch("camp");

  const userColumns = [
    { name: "user_code", label: "User ID" },
    { name: "user_name", label: "Username" },
    { name: "role_names", label: "Roles" },
    { name: "camp_names", label: "Camps" },
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    commonGetApi(API_ENDPOINTS.GET_ALL_NOTIFICATION_CAMPS, { organization_id: "888436C1-DCB6-EF11-88CD-7C1E52497BEE" }).then((res) => {
      let campDetails =
        res?.data?.data?.map((item) => ({
          ...item,
          label: item?.camp_name,
          value: item?.camp_details_id,
        })) || [];
      setCampData(campDetails);
    });
    // Remove initial user fetch - users will only be loaded when camp is selected
  }, []);

  // Prefill title and description if passed via navigation state
  useEffect(() => {
    const preset = location?.state || {};
    if (preset?.title) setValue("title", preset.title);
    if (preset?.description) setValue("description", preset.description);
  }, [location?.state, setValue]);

  useEffect(() => {
    if (selectedCampValue && selectedCampValue.length > 0) {
      setSelectedCamp(selectedCampValue);
      getUsersByCamp(selectedCampValue, navigator);
    } else {
      setSelectedCamp([]);
      setUserData([]);
      setNavigator((prev) => ({
        ...prev,
        totalRecords: 0,
        page: 1,
      }));
      // Don't call any API when no camp is selected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampValue]);

  // Common function to transform user data and update state
  const processUserData = (data) => {
    if (data?.status === 200) {
      const alluser = data?.data?.data;
      let userDetail =
        alluser?.map((user) => ({
          user_id: user.user_id,
          user_code: user.user_code,
          user_name: user.user_name,
          camp_names: user.camp_names ? user.camp_names.split(',').map((r) => r.trim()).join(', ') : '',
          camp_details_id:
            user.camps?.map((camp) => camp.camp_details_id) || [],
          role_names: user.role_names ? user.role_names.split(',').map((r) => r.trim()).join(', ') : ''
        })) || [];

      setUserData(userDetail);

      // Update total records from API response
      setNavigator((prev) => ({
        ...prev,
        totalRecords: data?.data?.total || 0,
        page: data?.data?.currentPage || 1,
      }));
    } else {
      // Handle empty or error response
      setUserData([]);
      setNavigator((prev) => ({
        ...prev,
        totalRecords: 0,
        page: 1,
      }));
    }
    setLoading(false);
  };

  const currentPageSelection = useMemo(() => {
    if (!Array.isArray(userData) || userData.length === 0) return [];
    if (!Array.isArray(selectedUserIds) || selectedUserIds.length === 0) return [];
    return userData.filter((u) => selectedUserIds.includes(u?.user_id));
  }, [userData, selectedUserIds]);

  const onUserSelectionChange = (e) => {
    const currentPageIds = (userData || [])
      .map((u) => u?.user_id)
      .filter(Boolean);

    const selectedOnPageIds = (e?.value || [])
      .map((u) => u?.user_id)
      .filter(Boolean);

    // Merge selection changes for this page, keep other pages' selections intact.
    setSelectedUserIds((prev) => {
      const prevIds = Array.isArray(prev) ? prev : [];
      const keptFromOtherPages = prevIds.filter((id) => !currentPageIds.includes(id));
      const merged = [...keptFromOtherPages];
      selectedOnPageIds.forEach((id) => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
  };

  const getUsersByCamp = async (
    campIds,
    navigator
  ) => {
    setLoading(true);
    if (!campIds || campIds.length === 0) {
      // When no camp is selected, don't call API, just clear data
      setUserData([]);
      setNavigator((prev) => ({
        ...prev,
        totalRecords: 0,
        page: 1,
      }));
      setLoading(false);
      return;
    }

    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        sortFields: navigator?.sortFields || [["user_name", "ASC"]],
        searchCriteria: {
          ...navigator?.searchCriteria, // user_name, role_name, camp_name (from search box)
          camp_details_id: Array.isArray(campIds) ? campIds : [campIds],
        },
      },
    };


    await commonPostApi(API_ENDPOINTS.GET_USER_WITH_ROLE, currentObj)
      .then(processUserData)
      .catch((err) => {
        console.log("error", "Something went wrong");
        setUserData([]);
        setLoading(false);
      });
  };

  const handleSearchChange = (value) => {
    setLoading(true);
    const newNavigator = {
      ...navigator,
      searchCriteria: {
        ...navigator?.searchCriteria,
        searchvalue: value,
      },
    };
    setNavigator(newNavigator);
    // Call API directly with updated search criteria to avoid stale state
    if (selectedCamp && selectedCamp.length > 0) {
      getUsersByCamp(selectedCamp, newNavigator);
    } else {
      // When no camp is selected, don't call API, just clear data
      setUserData([]);
      setNavigator((prev) => ({
        ...prev,
        totalRecords: 0,
        page: 1,
      }));
      setLoading(false);
    }
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    const newNavigator = {
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    };

    setNavigator(newNavigator);

    // Call common function with current pagination
    // Only call API if camp is selected
    if (selectedCamp && selectedCamp.length > 0) {
      getUsersByCamp(selectedCamp, newNavigator);
    } else {
      // When no camp is selected, don't call API, just clear data
      setUserData([]);
      setLoading(false);
    }
  };

  const onPageChange = (event) => {
    setLoading(true);
    const newNavigator = {
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    };
    setNavigator(newNavigator);

    if (selectedCamp && selectedCamp.length > 0) {
      getUsersByCamp(selectedCamp, newNavigator);
    } else {
      // When no camp is selected, don't call API, just clear data
      setUserData([]);
      setLoading(false);
    }
  };

  const onRowCountChange = (e) => {
    const newNavigator = {
      ...navigator,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    };
    setNavigator(newNavigator);
    // Call API with updated values immediately
    if (selectedCamp && selectedCamp.length > 0) {
      getUsersByCamp(selectedCamp, newNavigator);
    } else {
      // When no camp is selected, don't call API, just clear data
      setUserData([]);
      setNavigator((prev) => ({
        ...prev,
        totalRecords: 0,
        page: 1,
      }));
    }
  };

  const notificationButtons = [
    {
      type: "primarySuccessButton",
      label: "Send Notification",
      class: "pl-3 ",
      onClick: handleSubmit(async (data) => {
        try {
          if (selectedUserIds.length === 0) {
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.SELECT_USER);
            return;
          }

          setLoading(true);

          const notificationData = {
            notification_title: data.title,
            notification_description: data.description,
            notification_status: "active",
            scheduler_type:
              data.scheduler === "now" ? "immediate" : "scheduled",
            schedule_time:
              data.scheduler === "now"
                ? (() => {
                  const d = new Date();
                  const p = (n) => String(n).padStart(2, "0");
                  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
                    d.getDate()
                  )} ${p(d.getHours())}:${p(d.getMinutes())}:${p(
                    d.getSeconds()
                  )}`;
                })()
                : data.scheduleDateTime
                  ? typeof data.scheduleDateTime === "string"
                    ? data.scheduleDateTime
                    : (() => {
                      const d = new Date(data.scheduleDateTime);
                      const p = (n) => String(n).padStart(2, "0");
                      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
                        d.getDate()
                      )} ${p(d.getHours())}:${p(d.getMinutes())}:${p(
                        d.getSeconds()
                      )}`;
                    })()
                  : "2025-01-01 00:00:00",
            created_by: "6B0D0CB2-641C-440B-924A-809FD550FC67", // This should come from user context
            updated_by: "6B0D0CB2-641C-440B-924A-809FD550FC67", // This should come from user context
            camp_ids: Array.isArray(data.camp)
              ? data.camp.flat()
              : data.camp
                ? [data.camp]
                : [], // Handle multiselect and flatten array
            user_ids: selectedUserIds,
          };

          // Make API call to add notification
          const response = await commonPostApi(
            API_ENDPOINTS.ADD_NOTIFICATION,
            notificationData
          );

          if (response?.status === 200) {
            showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.NOTIFICATION.SENT);
            // Reset form and selections
            setSelectedUserIds([]);
            setSelectedCamp([]);
            // Clear all form fields
            setValue("title", "");
            setValue("description", "");
            setValue("camp", "");
            setValue("scheduler", "");
            setValue("scheduleDateTime", "");
            // You can add success message or redirect here
          } else {
            console.error("Failed to send notification:", response);
            // Handle error case
          }
        } catch (error) {
          console.error("Error sending notification:", error);
          // Handle error case
        } finally {
          setLoading(false);
        }
      }),
    },
    {
      type: "secondaryButton",
      label: "Discard",
      class: " mx-3",
      onClick: () => {
        reset({
          title: "",
          description: "",
          camp: "",
          scheduler_type: "",
          dateTime: ""
        })
        console.log("Discard notification");
        showToast(TOAST_TYPE.INFO, COMMON_MSG.INFO.CHANGES_DISCARDED);
      },
    },
  ];

  return (
    <>
      <div className="container mt-3 mb-3">
        <div className="px-2 md:px-3">
          <PrimaryHeading className={"mt-5 mb-3"} />
          <div style={{ color: "rgba(47, 43, 61, 0.9)" }}>
            <ButtonWithSearch searchHeaderLable={"Notification"} />
          </div>
          <form className="mt-4">
            <div className="row  flex-col gap-3">
              <div className="col">
                <div className="mb-3">
                  <div
                    className="text-xs md:text-sm"
                    style={{ color: "rgba(47, 43, 61, 0.9)" }}
                  >
                    1. Notification Details
                  </div>
                </div>

                <div className="md:flex ">
                  <div className="md:col-6">
                    <InputPrimaryComponents
                      control={control}
                      errors={errors}
                      type="text"
                      name="title"
                      label="Title"
                      placeholder="Enter the Title"
                      required
                      rules={{ required: "Title is required" }}
                    />
                  </div>

                  <div className="md:col-6">
                    <InputPrimaryComponents
                      control={control}
                      errors={errors}
                      type="text"
                      name="description"
                      label="Description"
                      placeholder="Enter the Description"
                      className="mb-4"
                      required
                      rules={{ required: "Description is required" }}
                    />
                  </div>
                </div>

                <div className="mt-3 md:col-6">
                  <div className="mb-3">
                    <div
                      className=" text-xs md:text-sm"
                      style={{ color: "rgba(47, 43, 61, 0.9)" }}
                    >
                      2. Camp Types
                    </div>
                  </div>
                  <div>
                    <InputPrimaryComponents
                      control={control}
                      errors={errors}
                      type="multiselect"
                      name="camp"
                      label="Camp"
                      options={campData}
                      placeholder="Select the Camp"
                      required
                      rules={{ required: "Camp is required" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div
          className="mt-3 container"
          style={{ color: "rgba(47, 43, 61, 0.9)" }}
        >
          <DataGrid
            header={
              <ButtonWithSearch
                searchHeaderLable={"User Details"}
                searchPlaceHolder={"Search Users"}
                isSearchEnabled={true}
                isCalenderEnabled={false}
                isButtonEnabledLeft={false}
                isButtonEnabledRight={false}
                onChange={handleSearchChange}
              />
            }
            column={userColumns}
            value={userData}
            resizableColumns={true}
            selection={currentPageSelection}
            onSelectionChange={onUserSelectionChange}
            selectionMode="multiple"
            selectable={true}
            dataKey="user_id"
            showDownloadButton={false}
            navigator={navigator}
            onSortClick={onSortClick}
            onPageChange={onPageChange}
            onRowCountChange={onRowCountChange}
            loading={loading}
            sortable={userData && userData.length > 0}
            tableClassName="wrap-columns"
          />
        </div>
        <div className="mt-5 px-2 md:px-3">
          <div className="mb-4">
            <div
              className="text-xs md:text-sm ml-1"
              style={{ color: "rgba(47, 43, 61, 0.9)" }}
            >
              3. Scheduler
            </div>
          </div>

          <div className="md:flex">
            <div className=" md:col-6">
              <InputPrimaryComponents
                control={control}
                errors={errors}
                type="dropdown"
                name="scheduler"
                label="Scheduler Type"
                options={[
                  { label: "Send by now", value: "now" },
                  { label: "Schedule by time", value: "time" },
                ]}
                placeholder="Select Scheduler Option"
                required
                rules={{ required: "Scheduler type is required" }}
              />
            </div>
            <div className="md:col-6 mt-3 md:mt-0">
              {watch("scheduler") === "time" && (
                <div>
                  <InputPrimaryComponents
                    control={control}
                    errors={errors}
                    type="calendar"
                    name="scheduleDateTime"
                    label="Date & Time"
                    placeholder="Select date and time"
                    showTime
                    hourFormat="24"
                    required
                    rules={{ required: "Date & time is required" }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-5">
            <AddButtonSection buttons={notificationButtons} />
          </div>
        </div>
      </div>
    </>
  );
};

export default SendNotification;
